import { useCallback, useRef, useState } from 'react';

import { THREAD_LIMITS, openingTasksApi, type ThreadLimits } from '../lib/api/openingTasks';
import {
  MAX_ROOM_VIDEOS,
  describeVideoFailure,
  limitVideoSlots,
  sendVideosInMessages,
  videoFailureNotice,
  videoMessageBody,
} from '../lib/room-media';
import { prepareFiles, type PendingFile } from './opening-tasks/utils';

export interface RoomVideosState {
  videos: PendingFile[];
  /** Ошибки последнего выбора (длиннее лимита, больше МБ, не влезло в форму). */
  errors: string[];
  /** Читаем длительность роликов. */
  preparing: boolean;
  limits: ThreadLimits;
  add: (picked: File[]) => Promise<void>;
  remove: (key: string) => void;
  clear: () => void;
}

/**
 * Видео помещения в формах «Оценка помещения» (новая заявка и повторная отправка):
 * выбранные ролики проверяются ДО отправки теми же правилами, что в переписке
 * (`prepareFiles`: размер по виду, длительность до 5 минут), а уходят после
 * создания заявки сообщением в её переписку (`uploadRoomVideos`).
 * Лимиты берём те же, что у переписки на бэке (`THREAD_LIMITS`).
 */
export function useRoomVideos(limits: ThreadLimits = THREAD_LIMITS): RoomVideosState {
  const [videos, setVideos] = useState<PendingFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [preparing, setPreparing] = useState(false);
  const countRef = useRef(0);
  countRef.current = videos.length;

  const add = useCallback(
    async (picked: File[]): Promise<void> => {
      if (!picked.length) return;
      const { take, error } = limitVideoSlots(picked, countRef.current);
      setPreparing(true);
      try {
        // Количество в форме ограничено своим потолком (MAX_ROOM_VIDEOS), а по сообщениям
        // видео делится позже — поэтому лимит «файлов в одном сообщении» здесь не нужен.
        const checked = take.length
          ? await prepareFiles(take, 0, { ...limits, maxFiles: take.length })
          : { accepted: [] as PendingFile[], errors: [] as string[] };
        setVideos((prev) => [...prev, ...checked.accepted].slice(0, MAX_ROOM_VIDEOS));
        setErrors(error ? [...checked.errors, error] : checked.errors);
      } finally {
        setPreparing(false);
      }
    },
    [limits],
  );

  const remove = useCallback((key: string): void => {
    setVideos((prev) => prev.filter((v) => v.key !== key));
  }, []);

  const clear = useCallback((): void => {
    setVideos([]);
    setErrors([]);
  }, []);

  return { videos, errors, preparing, limits, add, remove, clear };
}

/**
 * Отправка роликов в переписку заявки (после того как заявка создана). Не бросает:
 * возвращает текст для партнёра, если что-то не загрузилось, иначе null. Заявка к
 * этому моменту уже у куратора, поэтому текст — «Заявка отправлена, но видео не
 * загрузилось…», а не «Не удалось отправить заявку».
 */
export async function uploadRoomVideos(
  submissionId: string,
  videos: PendingFile[],
  limits: ThreadLimits = THREAD_LIMITS,
): Promise<string | null> {
  if (!videos.length) return null;
  const res = await sendVideosInMessages(
    videos,
    { sizeOf: (v) => v.file.size, maxFiles: limits.maxFiles },
    (chunk, index, total) =>
      openingTasksApi.sendMessage(submissionId, {
        body: videoMessageBody(index, total),
        files: chunk.map((v) => v.file),
        durations: chunk.map((v) => v.durationSec),
      }),
  );
  if (!res.failed) return null;
  return videoFailureNotice({ sent: res.sent, total: res.total, failure: describeVideoFailure(res.error) });
}
